import React, { useState, useEffect } from 'react';
import { Typography, Button, Skeleton, Empty } from 'antd';
import {
    ArrowLeftOutlined, ArrowRightOutlined, LinkOutlined,
    CloseOutlined, CheckCircleOutlined, UpOutlined, DownOutlined
} from '@ant-design/icons';
import { CompanyModel } from '../../data/company';
import { RootState } from '../../redux';
import { useSelector, useDispatch } from 'react-redux';
import { matchCompanies } from '../../redux/analysisSlice';
import type { AppDispatch } from '../../redux';
import ReactMarkdown from 'react-markdown';
import './CompanyModal.css';

const { Title, Paragraph } = Typography;

interface Props {
    onNext: () => void;
    onBack?: () => void;
}

const MatchCompanies: React.FC<Props> = ({
    onNext,
    onBack
}) => {
    const { services, companies } = useSelector((state: RootState) => state.analysis);
    const dispatch = useDispatch<AppDispatch>();
    const [activeCompany, setActiveCompany] = useState<CompanyModel | null>(null);
    const [modalVisible, setModalVisible] = useState(false);
    const serviceOfferings = services.serviceOfferings;
    const matchingCompanies = companies.matchingCompanies;
    useEffect(() => {
        const fetchCompanies = async () => {
            if (!serviceOfferings || matchingCompanies?.length) return;
            try {
                await dispatch(matchCompanies(serviceOfferings)).unwrap();
            } catch (err) {
                console.error("Error matching companies:", err);
            }
        };

        fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openCompanyModal = (company: CompanyModel) => {
        setActiveCompany(company);
        setModalVisible(true);

        document.body.style.overflow = 'hidden';
    };

    const closeCompanyModal = () => {
        setModalVisible(false);
        setActiveCompany(null);

        document.body.style.overflow = 'auto';
    };

    const [expanded, setExpanded] = useState(false);

    const truncateText = (text: string, maxLength?: number) => {
        if (!text) return '';
        text = text.split('\n')[0];
        if (!maxLength)
            return text;
        return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
    };

    // Sort by score
    const sortedCompanies = matchingCompanies
        ? [...matchingCompanies].sort((a, b) => b.match_score - a.match_score).filter(company => company.match_score >= 0.6)
        : [];

    const selectedCount = matchingCompanies?.filter(company => company.selected).length || 0;

    const formatScore = (score: number) => {
        return `${Math.round(score * 100)}%`;
    };

    const getWebsiteUrl = (website: string) => {
        if (!website) return '';
        return website.startsWith('http') ? website : `https://${website}`;
    };

    return (
        <>
            <div className="matches-list">
                <Title level={4} className="panel-title">Matched Portfolio Companies</Title>
                <div className="llm-reasoning-container">
                    <p className="label">
                        Interpretation
                    </p>
                    <Paragraph className="llm-reasoning">
                        <div className="service-offerings-preview" style={{ display: 'flex' }}>
                            <div className="preview-content">
                                {expanded ? (
                                    <ReactMarkdown>{serviceOfferings || "No service offerings available"}</ReactMarkdown>
                                ) : (
                                    <ReactMarkdown>{truncateText(serviceOfferings) || "No service offerings available"}</ReactMarkdown>
                                )}
                            </div>
                            <Button
                                type="text"
                                icon={expanded ? <UpOutlined /> : <DownOutlined />}
                                onClick={() => setExpanded(!expanded)}
                                className="material-button"
                            />
                        </div>
                    </Paragraph>
                </div>
                <Paragraph style={{ color: "#6b7280"}}>
                    Select companies to include in candidate search
                </Paragraph>

                {
                    companies.loading === 'idle' || companies.loading === 'pending' ? (
                        <div className="company-grid">
                            {[1, 2, 3, 4, 5, 6].map(i => (
                                <div key={i} className="company-card skeleton">
                                    <div className="card-header">
                                        <Skeleton.Input active size="small" style={{ marginBottom: '8px' }} />
                                        <Skeleton.Input active size="small" />
                                    </div>
                                    <Skeleton.Input active size="small" style={{ width: '100%', height: 40 }} />
                                </div>
                            ))}
                        </div>
                    ) : matchingCompanies?.length === 0 ? (
                        <Empty
                            description="No companies matched"
                            className="empty-state"
                        />
                    ) : (
                        <>
                            <div className="company-grid">
                                {sortedCompanies.map(company => (
                                    <div
                                        key={company._id}
                                        className="company-card"
                                        onClick={() => openCompanyModal(company)}
                                    >
                                        <div className="card-header">
                                            <div className="company-info">
                                                <div className="company-name">{company.name}</div>
                                                <div className="company-score">
                                                    <span className="score-pill">{formatScore(company.match_score)}</span>
                                                    <span className="score-label">Match Score</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="card-content">
                                            {company.website && (
                                                <div className="website-container">
                                                    <LinkOutlined className="link-icon" />
                                                    <a
                                                        href={getWebsiteUrl(company.website)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="company-website"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        Visit website
                                                    </a>
                                                </div>
                                            )}

                                            <div className="company-offerings">
                                                {company.matching_offerings.slice(0, 3).map((offering, idx) => (
                                                    <span key={`${company._id}-${idx}`} className="offering-tag">
                                                        {offering}
                                                    </span>
                                                ))}
                                                {company.matching_offerings.length > 3 && (
                                                    <span className="offering-tag">
                                                        +{company.matching_offerings.length - 3} more
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <button
                                            className="expand-button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                openCompanyModal(company);
                                            }}
                                        >
                                            <span>View details</span>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </>
                    )
                }

            </div >
            {/* Company Details Modal */}
            < div className={`company-modal-overlay ${modalVisible ? 'active' : ''}`} onClick={closeCompanyModal} >
                {activeCompany && (
                    <div className="company-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">{activeCompany.name}</h3>
                            <button className="modal-close" onClick={closeCompanyModal}>
                                <CloseOutlined />
                            </button>
                        </div>

                        <div className="modal-content">
                            <div className="modal-stats">
                                <div className="stat-item">
                                    <div className="stat-label">Match Score</div>
                                    <div className="stat-value highlighted">{formatScore(activeCompany.match_score)}</div>
                                </div>

                                <div className="stat-item">
                                    <div className="stat-label">Matching Criteria</div>
                                    <div className="stat-value">{activeCompany.matching_offerings.length}</div>
                                </div>
                            </div>

                            {activeCompany.match_reason && (
                                <div className="modal-section">
                                    <h4 className="section-title">Match Reason</h4>
                                    <p className="description-text">{activeCompany.match_reason}</p>
                                </div>
                            )}

                            {activeCompany.description && (
                                <div className="modal-section">
                                    <h4 className="section-title">Company Description</h4>
                                    <p className="description-text">{activeCompany.description}</p>
                                </div>
                            )}

                            <div className="modal-section">
                                <h4 className="section-title">Service Offerings</h4>
                                <div className="offering-tags">
                                    {activeCompany.matching_offerings.map((offering, idx) => (
                                        <span key={`modal-offering-${idx}`} className="modal-offering-tag matched">
                                            <CheckCircleOutlined style={{ marginRight: '4px' }} /> {offering}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {activeCompany.website && (
                                <a
                                    href={getWebsiteUrl(activeCompany.website)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="website-link"
                                >
                                    <LinkOutlined /> Visit company website
                                </a>
                            )}
                        </div>

                        <div className="modal-footer">
                            <Button
                                type="primary"
                                className="button-primary"
                                onClick={closeCompanyModal}
                            >
                                Close
                            </Button>
                        </div>
                    </div>
                )}
            </div >

            <div className="action-bar">
                <span className="selection-count">
                    {selectedCount} {selectedCount === 1 ? 'company' : 'companies'} found
                </span>
                <div className="action-buttons">
                    <Button
                        icon={<ArrowLeftOutlined />}
                        className="button button-secondary"
                        onClick={onBack}
                    >
                        Back
                    </Button>
                    <Button
                        type="primary"
                        onClick={onNext}
                        disabled={!selectedCount}
                        className="button button-primary"
                        icon={<ArrowRightOutlined />}
                    >
                        Find Matching Employees
                    </Button>
                </div>
            </div>
        </>
    );
};

export default MatchCompanies;
